/**
 * Comparison — 3 variants (us_vs_them, matrix, before_after)
 */
import type { ComparisonContent } from '../schemas/comparison';
import {
  Body,
  CheckIcon,
  Eyebrow,
  H2,
  H3,
  SectionFrame,
} from './primitives';

interface CP {
  content: ComparisonContent;
}

export function ComparisonUsVsThem({ content }: CP) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
          {content.subheadline && <Body>{content.subheadline}</Body>}
        </div>
        <div className="ls-grid ls-grid-2" style={{ gap: 24 }}>
          <div
            className="ls-card"
            style={{
              border: '2px solid var(--ls-accent)',
              background: 'var(--ls-accent-soft)',
            }}
          >
            <H3 style={{ marginBottom: 16 }}>{content.usLabel}</H3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {content.rows.map((r, i) => (
                <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--ls-accent)', flexShrink: 0, marginTop: 2 }}>
                    <CheckIcon size={16} />
                  </span>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{r.feature}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--ls-text-muted)' }}>{r.us}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="ls-card">
            <H3 style={{ marginBottom: 16, color: 'var(--ls-text-muted)' }}>{content.themLabel}</H3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {content.rows.map((r, i) => (
                <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', opacity: 0.7 }}>
                  <span style={{ color: 'var(--ls-text-muted)', flexShrink: 0, marginTop: 2 }}>—</span>
                  <div>
                    <div style={{ fontSize: '0.9rem' }}>{r.feature}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--ls-text-muted)' }}>{r.them}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </SectionFrame>
  );
}

export function ComparisonMatrix({ content }: CP) {
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr>
                <th style={{ padding: 16, textAlign: 'left' }} />
                <th style={{ padding: 16, textAlign: 'left', fontWeight: 600 }}>{content.usLabel}</th>
                <th style={{ padding: 16, textAlign: 'left', color: 'var(--ls-text-muted)', fontWeight: 500 }}>{content.themLabel}</th>
              </tr>
            </thead>
            <tbody>
              {content.rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ padding: 16, fontWeight: 500, borderBottom: '1px solid var(--ls-border)' }}>{r.feature}</td>
                  <td style={{ padding: 16, color: 'var(--ls-accent)', borderBottom: '1px solid var(--ls-border)' }}>{r.us}</td>
                  <td style={{ padding: 16, color: 'var(--ls-text-muted)', borderBottom: '1px solid var(--ls-border)' }}>{r.them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionFrame>
  );
}

export function ComparisonBeforeAfter({ content }: CP) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {content.rows.map((r, i) => (
            <div key={i} className="ls-card ls-before-after-row">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--ls-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {content.themLabel}
                </div>
                <div>{r.them}</div>
              </div>
              <div className="ls-before-after-arrow" style={{ color: 'var(--ls-accent)' }}>→</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--ls-accent)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {content.usLabel}
                </div>
                <div style={{ fontWeight: 500 }}>{r.us}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export const comparisonVariants = {
  us_vs_them: ComparisonUsVsThem,
  matrix: ComparisonMatrix,
  before_after: ComparisonBeforeAfter,
};
